import React, { useEffect, useState } from 'react';
import { RouteComponentProps, withRouter, StaticContext, useParams } from 'react-router';
import Label from '../../components/Label';
import { axiosKK } from '../../services/networkRequest';
import {
  AdminOxfordPackageOrder,
  AdminOxfordPackageOrdersResponse,
} from '../../models/APIModels';
import Moment from 'moment';
import { PageHeader } from '../../components/admin/Shared';
import { toMoney } from '../../utilities/MoneyConversion';
import { oxfordPaymentStatusLabel } from '../../utilities/OxfordPackageStatus';

interface OxfordOrderParams {
  id: string;
}

interface OxfordOrderComponentState {
  order?: AdminOxfordPackageOrder;
}

function AdminOxfordPackageOrderDetailComponent({
  history,
}: RouteComponentProps<undefined, StaticContext, OxfordOrderComponentState>) {
  const { id } = useParams<OxfordOrderParams>();
  const [order, setOrder] = useState<AdminOxfordPackageOrder | undefined>(
    history.location?.state?.order
  );
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!order) {
      // Opened directly or refreshed: there is no single-order endpoint, so find
      // it in the admin list.
      let cancelled = false;
      axiosKK
        .get<AdminOxfordPackageOrdersResponse>('/admin/oxford-packages/orders')
        .then(({ data }) => {
          if (cancelled) return;
          const match = (data.orders || []).find((o) => String(o.id) === id);
          if (match) setOrder(match);
          else setNotFound(true);
        })
        .catch((error) => {
          console.error('Error fetching Oxford package order:', error);
          if (!cancelled) setNotFound(true);
        });
      return () => { cancelled = true; };
    }
  }, [order, id]);

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'succeeded':
        return '#4CAF50';
      case 'failed':
      case 'canceled':
        return '#F44336';
      default:
        return '#FFA726';
    }
  };

  if (!order) {
    return (
      <>
        <PageHeader
          title={notFound ? 'Order not found' : 'Loading...'}
          onBack={() => history.push('/admin/dashboard')}
        />
        <p>
          {notFound
            ? `Oxford package order #${id} could not be loaded.`
            : 'Loading Oxford package order...'}
        </p>
      </>
    );
  }

  const customer = order.customer;
  const addressLines = [
    order.billingLine1,
    order.billingLine2,
    order.billingCity,
    order.billingPostcode,
    order.billingCountry,
  ].filter((line) => line && line.trim().length > 0);

  return (
    <>
      <PageHeader
        title={`Oxford Package Order #${order.id}`}
        onBack={() => history.push('/admin/dashboard')}
      />

      <section className="print-doc-and-items-block">
        <div className="print-doc admin-print-doc">
          <div className="top-border">Order Information</div>
          <div className="second-border">
            <p className="roomPack">
              <span
                style={{
                  backgroundColor: getStatusBadgeColor(order.paymentStatus),
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '4px',
                  fontWeight: 'bold',
                }}
              >
                {oxfordPaymentStatusLabel(order.paymentStatus)}
              </span>
            </p>
            <p className="date">
              Ordered: {Moment(order.createdAt).format('dddd, MMM Do YYYY, HH:mm')}
            </p>
          </div>
          <div className="basic-block">
            <div className="id-city-requirement">
              <div className="id-and-city">
                <p className="id">#{order.id}</p>
                <p className="city">{order.college}</p>
                {customer && (
                  <div
                    onClick={() => history.push(`/admin/customer/${customer.id}`)}
                    className="name"
                    style={{ cursor: 'pointer' }}
                  >
                    <p>View Customer Profile</p>
                  </div>
                )}
              </div>
            </div>
            <div className="addresses-block">
              <div className="each">
                <Label>Package:</Label>
                <p>{order.packageName}</p>
              </div>
              <div className="each">
                <Label>Price:</Label>
                <p>{toMoney(order.packagePrice)}</p>
              </div>
              <div className="each">
                <Label>College:</Label>
                <p>{order.college}</p>
              </div>
            </div>
            <div className="addresses-block">
              <div className="each">
                <Label>Customer Name:</Label>
                <p>
                  {customer
                    ? `${customer.firstName} ${customer.lastName}`.trim()
                    : 'No account attached'}
                </p>
              </div>
              <div className="each">
                <Label>Customer Email:</Label>
                <p>
                  {customer?.email ? (
                    <a href={`mailto:${customer.email}`}>{customer.email}</a>
                  ) : (
                    'Not provided'
                  )}
                </p>
              </div>
              <div className="each">
                <Label>Customer Phone:</Label>
                <p>
                  {customer?.phoneNumber ? (
                    <a href={`tel:${customer.phoneNumber}`}>{customer.phoneNumber}</a>
                  ) : (
                    'Not provided'
                  )}
                </p>
              </div>
            </div>
            <div className="addresses-block">
              <div className="each">
                <Label>Billing Address:</Label>
                {addressLines.map((line, index) => (
                  <p key={index}>{line}</p>
                ))}
              </div>
              <div className="each">
                <Label>Stripe Payment:</Label>
                <p>{order.stripePaymentIntentId || 'None'}</p>
              </div>
              <div className="each">
                <Label>HubSpot Deal:</Label>
                <p>{order.hubspotId || 'None'}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default withRouter(AdminOxfordPackageOrderDetailComponent as any);
